import {
  Badge,
  Card,
  CardContent,
  cn,
  statusOkDotClass,
} from '@up2stay/up2promo-ui';
import { AnimatePresence, motion } from 'motion/react';

import { formatRelativeTime } from '@/core/components/leads/lead-detail/format-relative-time';
import { shellPaths } from '@/core/router/app-paths';

import { dashboardSpringSnappy } from './dashboard-board-primitives';
import {
  statusBoardCardClass,
  statusBoardCardPadClass,
  statusBoardSectionTitleClass,
} from './dashboard-board-surfaces';
import type { DashboardLiveScan } from './dashboard-status-types';

export function DashboardLiveCard({
  liveScans,
  highlightedScanId,
  connectionState,
  locale,
  onOpen,
  reduceMotion,
  labels,
}: {
  liveScans: DashboardLiveScan[];
  highlightedScanId: string | null;
  connectionState: string;
  locale: string;
  onOpen: (href: string) => void;
  reduceMotion: boolean;
  labels: {
    title: string;
    empty: string;
    connecting: string;
    connected: string;
    error: string;
    scanSub: string;
  };
}) {
  const statusLabel =
    connectionState === 'open'
      ? labels.connected
      : connectionState === 'connecting'
        ? labels.connecting
        : connectionState === 'error'
          ? labels.error
          : labels.empty;

  return (
    <section
      aria-labelledby="dashboard-live-heading"
      className="flex h-full min-w-0 flex-col"
    >
      <Card className={cn(statusBoardCardClass, 'h-full')}>
        <CardContent
          className={cn('flex flex-col gap-1.5', statusBoardCardPadClass)}
        >
          <div className="mb-1 flex items-center gap-2.5">
            <h2
              id="dashboard-live-heading"
              className={cn('min-w-0 flex-1', statusBoardSectionTitleClass)}
            >
              {labels.title}
            </h2>
            <Badge
              variant={
                connectionState === 'open'
                  ? 'success'
                  : connectionState === 'error'
                    ? 'destructive'
                    : 'secondary'
              }
              className="gap-1.5 text-[11px] font-semibold"
            >
              <span
                className={cn(
                  'size-1.5',
                  connectionState === 'open'
                    ? statusOkDotClass
                    : 'rounded-full bg-current',
                  connectionState === 'open' &&
                    'animate-pulse motion-reduce:animate-none',
                )}
              />
              {statusLabel}
            </Badge>
          </div>

          {liveScans.length === 0 ? (
            <p className="py-6 text-center text-[13px] text-muted-foreground">
              {labels.empty}
            </p>
          ) : (
            <ol className="list-none p-0">
              <AnimatePresence initial={false} mode="popLayout">
                {liveScans.slice(0, 6).map((scan) => {
                  const isHot = scan.id === highlightedScanId;
                  return (
                    <motion.li
                      key={scan.id}
                      layout={!reduceMotion}
                      initial={reduceMotion ? false : { opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={dashboardSpringSnappy}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          onOpen(shellPaths.moreProductDetail(scan.productId))
                        }
                        className={cn(
                          'flex w-full gap-3 border-t border-border/60 py-2.5 text-left first:border-t-0',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                          isHot && 'bg-[color:var(--brand-500)]/[0.06]',
                        )}
                      >
                        <span
                          className="mt-1.5 size-2 shrink-0 rounded-full bg-[color:var(--brand-500)]"
                          aria-hidden
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[14px] font-medium text-foreground">
                            {scan.name}
                          </span>
                          <span className="block truncate text-[12px] text-muted-foreground">
                            {labels.scanSub}
                          </span>
                        </span>
                        <span className="shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground">
                          {formatRelativeTime(scan.scannedAt, locale)}
                        </span>
                      </button>
                    </motion.li>
                  );
                })}
              </AnimatePresence>
            </ol>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
